import {
  ChevronDownIcon,
  EditIcon,
  NetworkIcon,
  PlusIcon,
  TerminalIcon,
  VariableIcon,
  WrenchIcon,
} from "lucide-react";
import { useState } from "react";

import { usePlayground } from "../Playground/playground-store";

import { useFunctionDialogContext } from "@/components/FunctionsModal";
import BuiltInToolsDialog from "@/components/FunctionsModal/BuiltInToolsDialog";
import { FunctionsList } from "@/components/FunctionsModal/FunctionsList";
import { getBuiltInToolsForProvider } from "@/components/FunctionsModal/built-in-tools";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { prompt } from "@/schemas";
import {
  BuiltInTool,
  BuiltInToolProvider,
  Function_,
  FunctionsType,
  RegistryTool,
  ToolVariable,
  functionsTypes,
} from "@/types";
import { isBuiltInTool, isMcpTool, isToolVariable } from "@/utils/utils";

const SelectFunctionType = () => {
  const { functionsType, setFunctionsType } = useFunctionDialogContext();

  const { provider } = usePlayground();

  // Only show 'functions' option if it's already in use (legacy support)
  // Otherwise, only show 'tools' (the default and recommended option)
  let filteredFunctionTypes: readonly string[];
  if (provider === "google") {
    filteredFunctionTypes = ["tools"] as const;
  } else if (functionsType === "functions") {
    filteredFunctionTypes = functionsTypes;
  } else {
    filteredFunctionTypes = ["tools"] as const;
  }

  return (
    <div className="flex items-center gap-2">
      <Label className="font-bold">Functions Type: </Label>
      <Select
        value={functionsType}
        onValueChange={(type: FunctionsType) => setFunctionsType?.(type)}
        disabled={!setFunctionsType}
      >
        <SelectTrigger className="max-w-xs capitalize">
          <SelectValue placeholder="Functions Type" />
        </SelectTrigger>
        <SelectContent>
          {filteredFunctionTypes.map(type => (
            <SelectItem key={type} value={type} className="capitalize">
              {type}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const AddFunctionButtons = () => {
  const {
    functionsType,
    setIsAddingFunction,
    setIsShowingBuiltInTools,
    setIsShowingMcpTool,
    setIsShowingRegistryTools,
    functions,
    setFunctions,
  } = useFunctionDialogContext();
  const playground = usePlayground();
  const [isToolVariableOpen, setIsToolVariableOpen] = useState(false);
  const [variableName, setVariableName] = useState("");

  const currentProvider = playground.provider?.toLowerCase() as BuiltInToolProvider | undefined;
  const { model } = playground;
  const availableBuiltInTools = currentProvider
    ? getBuiltInToolsForProvider(currentProvider, {
        modelName: model?.name,
        baseModel: model?.base_model ?? undefined,
        apiType: model?.api_type,
      })
    : [];

  if (functionsType === "functions") {
    return (
      <Button size="sm" onClick={() => setIsAddingFunction(true)}>
        <PlusIcon className="mr-2 inline h-4 w-4" />
        Add new function
      </Button>
    );
  }

  const hasBuiltInTools = availableBuiltInTools.length > 0;

  const handleAddVariable = () => {
    if (!variableName.trim() || !setFunctions) {
      return;
    }
    const toolVariable: ToolVariable = { type: "variable", name: variableName.trim() };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFunctions([...functions, toolVariable as any]);
    setVariableName("");
    setIsToolVariableOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex items-center divide-x divide-blue-300">
        <Button size="sm" className="rounded-r-none" onClick={() => setIsAddingFunction(true)}>
          <PlusIcon className="mr-2 inline h-4 w-4" />
          Add Tool
        </Button>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="rounded-l-none px-2">
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[160px]">
            <DropdownMenuItem className="w-full" onClick={() => setIsAddingFunction(true)}>
              <EditIcon className="mr-2 inline h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">Custom Tool</span>
            </DropdownMenuItem>
            {hasBuiltInTools && (
              <DropdownMenuItem className="w-full" onClick={() => setIsShowingBuiltInTools(true)}>
                <WrenchIcon className="mr-2 inline h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">Built-In Tool</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="w-full" onClick={() => setIsShowingMcpTool(true)}>
              <NetworkIcon className="mr-2 inline h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">MCP Server</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="w-full" onClick={() => setIsToolVariableOpen(true)}>
              <VariableIcon className="mr-2 inline h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">Tool Variable</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="w-full" onClick={() => setIsShowingRegistryTools(true)}>
              <TerminalIcon className="mr-2 inline h-4 w-4 shrink-0" />
              <span className="whitespace-nowrap">From Registry</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {isToolVariableOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-md border bg-white p-4 shadow-md">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Variable Name</Label>
            <p className="text-xs text-gray-500">
              Tools will be injected from this input variable at runtime.
            </p>
            <Input
              value={variableName}
              onChange={e => setVariableName(e.target.value)}
              placeholder="e.g. dynamic_tools"
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleAddVariable();
                }
              }}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setIsToolVariableOpen(false);
                  setVariableName("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="flex-1"
                onClick={handleAddVariable}
                disabled={!variableName.trim()}
              >
                <PlusIcon className="mr-2 inline h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SelectFunctionCall = () => {
  const { functionCallValue, setFunctionCall, functions, functionsType, toolChoiceValue } =
    useFunctionDialogContext();
  const playground = usePlayground();
  const provider = playground.provider?.toLowerCase();
  const isOpenAI = provider === "openai" || provider === "openai.azure";
  const isBedrock = provider === "amazon.bedrock";
  // OpenRouter's tool_choice surface is OpenAI-compatible (auto/none/required +
  // named function), so it gets the same options as OpenAI.
  const isOpenRouter = provider === "openrouter";
  const isOpenAICompatible = isOpenAI || isOpenRouter;

  let placeholder = "Select tool choice";
  if (functionsType === "functions") {
    placeholder = "Select function call";
  }

  // Filter available options based on provider
  // OpenAI / OpenRouter: none, auto, required + specific tools
  // Bedrock: auto, any + specific tools (none not in Converse API)
  // Others: none, auto + specific tools
  const getAvailableOptions = () => {
    if (isOpenAICompatible) {
      return prompt.functionCall.filter(option => option !== "any");
    }
    if (isBedrock) {
      return prompt.functionCall.filter(option => option !== "required" && option !== "none");
    }
    return prompt.functionCall.filter(option => option !== "required" && option !== "any");
  };

  const availableOptions = getAvailableOptions();

  const getValidStringOptions = () => {
    if (isOpenAICompatible) {
      return ["auto", "none", "required"];
    }
    if (isBedrock) {
      // Bedrock: auto, any; none not in Converse API so omit from list
      return ["auto", "any"];
    }
    return ["auto", "none"];
  };

  return (
    <Select
      value={functionCallValue || toolChoiceValue}
      onValueChange={functionCall => {
        const validStringOptions = getValidStringOptions();
        if (validStringOptions.includes(functionCall)) {
          setFunctionCall!(functionCall as (typeof prompt.functionCall)[number]);
        } else {
          setFunctionCall!({
            name: functionCall,
          });
        }
      }}
    >
      <SelectTrigger className="max-w-xs rounded-sm py-3">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="rounded-sm">
        <SelectGroup>
          {availableOptions.map(functionCall => (
            <SelectItem key={functionCall} value={functionCall}>
              {functionCall}
            </SelectItem>
          ))}
          {functions
            .filter(
              _function =>
                !isToolVariable(_function as unknown as ToolVariable) && !isMcpTool(_function),
            )
            .map(_function => {
              const isRegistry =
                "type" in _function &&
                (_function as unknown as Record<string, unknown>).type === "registry";
              const regTool = isRegistry ? (_function as unknown as RegistryTool) : null;
              const builtIn =
                !isRegistry && isBuiltInTool(_function as Function_)
                  ? (_function as unknown as BuiltInTool)
                  : null;
              const getValue = () => {
                if (isRegistry) {
                  return (
                    regTool?._resolved_name ??
                    playground.getRegistryToolName(regTool!.tool_registry_id)
                  );
                }
                if (builtIn) {
                  return builtIn.type;
                }
                return "name" in _function ? _function.name : "";
              };
              const value = getValue();
              const label = builtIn ? builtIn.name : value;
              if (!value) {
                return null;
              }
              return (
                <SelectItem key={value} value={value} className="font-mono">
                  {`>_ `}
                  {label}
                </SelectItem>
              );
            })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const FunctionOverviewDialog = () => {
  const {
    isEditing,
    functionCallValue,
    toolChoiceValue,
    functionsType,
    isShowingBuiltInTools,
    functions,
  } = useFunctionDialogContext();
  const rawDisplayValue = functionsType === "tools" ? toolChoiceValue : functionCallValue;
  const displayValue = (() => {
    if (rawDisplayValue === undefined) {
      return undefined;
    }
    const builtIn = functions.find(
      f => isBuiltInTool(f as Function_) && (f as unknown as BuiltInTool).type === rawDisplayValue,
    );
    return builtIn ? (builtIn as unknown as BuiltInTool).name : rawDisplayValue;
  })();
  const playground = usePlayground();
  const isBedrock = playground.provider?.toLowerCase() === "amazon.bedrock";
  const isAnthropicProvider =
    playground.provider === "anthropic" ||
    (playground.provider === "vertexai" &&
      (playground.model?.name?.toLowerCase().includes("claude") ||
        playground.model?.base_model?.toLowerCase().includes("claude"))) ||
    (playground.provider === "amazon.bedrock" &&
      (playground.model?.name?.toLowerCase().includes("anthropic") ||
        playground.model?.base_model?.toLowerCase().includes("anthropic")));

  const functionsTypeLabel = functionsType === "functions" ? "function_call:" : "tool_choice";

  if (isShowingBuiltInTools) {
    return <BuiltInToolsDialog />;
  }

  return (
    <>
      <div className="flex flex-col">
        {functionsType === "functions" && <SelectFunctionType />}
        {displayValue !== undefined || isEditing ? (
          <>
            <div
              className={`flex flex-row items-center gap-x-2 pt-2 text-sm ${
                isEditing ? "pb-3" : "pb-1"
              }`}
            >
              <div className="font-mono">{functionsTypeLabel}</div>
              <div>
                {isEditing ? (
                  <SelectFunctionCall />
                ) : (
                  <div className="border-gray-500 font-mono text-sm text-gray-700">
                    <TerminalIcon className="mr-1 inline h-4 w-4" />
                    {displayValue}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400">
              {isBedrock
                ? `Controls how the model responds to tool calls: "auto" (model can choose), "any" (model must use one of the tools), or select a function to force the model to call it.`
                : `Controls how the model responds to function calls, with options for "none" (model doesn't call a function), "auto" (model can choose between end-user or function call), and specifying a function forces the model to call it.`}
              <a
                href={
                  isBedrock
                    ? "https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_ToolChoice.html"
                    : "https://platform.openai.com/docs/api-reference/chat/create#chat/create-function_call"
                }
                target="_blank"
                rel="noreferrer"
                className="pl-1 text-xs text-blue-400 hover:text-blue-300"
              >
                Learn more here.
              </a>
            </p>
          </>
        ) : null}
      </div>
      <div>
        <div className="mt-3 flex items-center pb-1">
          <Label className="text-md flex-1 text-gray-800">Function definitions:</Label>
          {isEditing && <AddFunctionButtons />}
        </div>
        <FunctionsList isAnthropicProvider={isAnthropicProvider} />
      </div>
    </>
  );
};

export default FunctionOverviewDialog;
